import { createAsyncThunk } from '@reduxjs/toolkit';
import PaymentHooks from '../utils/PaymentHooks';
import { getSelectedPaymentMethods } from '../selectors';
import { getEftposConfig } from '../selectors/config';
import {
  EFTPOS_STATUS_UPDATE,
  FAILURE_REASON,
  WINDCAVE_NOTIFICATION,
} from '../constants';
import Logger from '../utils/Logger';
import delay, { centsToDollars } from '../utils/misc';
import { sale } from './sale';
import { updateSelectedPaymentMethod } from '../reducers/currentOrder/selectedPaymentMethods';
import { PAYMENT_METHOD } from '../constants/paymentMethod';
import getCurrentOrder from '../selectors/getCurrentOrder';
import moment from 'moment';
import parseWindcaveReceipt from '../utils/processors/processWindcaveReceipt';

export const windcaveShellPayments = createAsyncThunk(
  '$windcaveShellPayments',
  async (
    data: {
      amount: number;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    let throw_reason: FAILURE_REASON | string | undefined;

    try {
      const currentLocationId = getCurrentOrder(
        getState() as EntireFrontendState,
      ).locationId;
      const {
        terminalId,
        paymentProvider,
        paymentEnvironment,
        hostAddress,
        apiKey,
        eposIdentifier,
        deviceId,
      } = getEftposConfig(getState() as EntireFrontendState);

      Logger.logUpstream({
        DeviceID: deviceId as string,
        StoreID: currentLocationId!,
        Action: 'BEFORE_PAYMENT',
        DateTime: new Date().toLocaleString('en', {
          timeZone: 'Australia/Sydney',
        }),
        Data: {
          Endpoint: 'N/A',
          SentBody: JSON.stringify({
            terminalId,
            paymentProvider,
            paymentEnvironment,
            hostAddress,
            apiKey,
            eposIdentifier,
            deviceId,
          }),
          ResponseCode: null,
          ResponseBody: null,
          ResponseTime: null,
        },
      });

      const { amount } = data;
      // This thunk will call the windcave init inside the shell
      // and the rest of the flow will be handled by the shell awaiting
      // the response from windcave which will be stored in shellStore and
      // read by the polling function inside the thunk

      window._windcave_init(centsToDollars(amount));
      const windcavePollStart = moment();

      // Set to 300 for 60s period (Note: this number is based on the delay you set inside the recursive check)
      // Tyro will timeout after 60s so safe to add it for a little longer
      let repeat = 220; // fail safe incase we don't catch some error we should break the recursion
      const windcave_response: any = async () => {
        // Note: this must inside the func otherwise it won't be detected
        const hook: any = PaymentHooks.get(EFTPOS_STATUS_UPDATE);

        const windcavePacket =
          window.shellStore.windcaveTransaction &&
          JSON.parse(window.shellStore.windcaveTransaction);
        const { result, reason } = windcavePacket;

        if (hook) {
          if (result?.Scr?.TxnStatusId) {
            hook({
              statusMsg:
                result?.Scr?.TxnStatusId === 8
                  ? WINDCAVE_NOTIFICATION[result?.Scr?.TxnStatusId] +
                    result?.Scr?.DL2
                  : WINDCAVE_NOTIFICATION[result?.Scr?.TxnStatusId],
              txnStarted: true,
            });
          } else {
            hook({
              statusMsg: 'Transaction in progress — Please refer to terminal',
              txnStarted: true,
            });
          }
        }

        if (repeat < 0) {
          Logger.log('-----  ----- Windcave Fallback ----- ----', 'info');
          throw_reason = 'fallback';
        }

        if (reason) {
          Logger.log('-----  ----- Windcave Error ----- ----', 'info');
          Logger.log(JSON.stringify(reason));
          Logger.log(JSON.stringify(result));
          Logger.log('----- ----- ----- ----- ----- -----', 'info');
          throw_reason = reason;
        } else if (result && result.Scr?.DL1 === 'APPROVED') {
          Logger.log('-----  ----- Windcave Success Result ----- ----', 'info');
          Logger.log(JSON.stringify(result));
          Logger.log('----- ----- ----- ----- ----- -----', 'info');

          const subPayments = getSelectedPaymentMethods(
            getState() as EntireFrontendState,
          );
          let subPayment = subPayments.filter(
            subPayment => subPayment.method === PAYMENT_METHOD.EFTPOS,
          )[0];
          subPayment = {
            ...subPayment,
            method: subPayment.method,
            amount: subPayment.amount,
            cardType: result?.Scr?.Result?.CT,
            receiptText: parseWindcaveReceipt(result?.Scr?.Rcpt),
            referenceNumber: result?.Scr.Result?.TR,
            authorisationCode: String(result?.Scr?.Result?.AC),
            Receipt: result?.Scr.Result?.TR,
          };

          const getLatestPaymentMethods = getSelectedPaymentMethods(
            getState() as EntireFrontendState,
          );
          Logger.log('-----  ----- Updated Payment Methods ----- ----', 'info');
          Logger.log(JSON.stringify(getLatestPaymentMethods));
          Logger.log('----- ----- ----- ----- ----- -----', 'info');

          dispatch(updateSelectedPaymentMethod(subPayment));
          // Step 5: Return a successful flow when payment is processed

          const windcavePollEnd = moment();
          const windcavePollResponseTime =
            windcavePollEnd.diff(windcavePollStart);

          Logger.logUpstream({
            DeviceID: deviceId as string,
            StoreID: currentLocationId!,
            Action: 'AFTER_PAYMENT',
            DateTime: new Date().toLocaleString('en', {
              timeZone: 'Australia/Sydney',
            }),
            Data: {
              Endpoint: 'N/A',
              SentBody: null,
              ResponseCode: result && 200,
              ResponseBody: JSON.stringify({ result, reason }),
              ResponseTime: windcavePollResponseTime,
            },
          });

          dispatch(
            sale({ route: 'checkout', authenticationMethod: 'trusted' }),
          ).unwrap();

          return;
        }

        if (throw_reason) {
          const windcavePollEnd = moment();
          const windcavePollResponseTime =
            windcavePollEnd.diff(windcavePollStart);

          Logger.logUpstream({
            DeviceID: deviceId as string,
            StoreID: currentLocationId!,
            Action: 'AFTER_PAYMENT',
            DateTime: new Date().toLocaleString('en', {
              timeZone: 'Australia/Sydney',
            }),
            Data: {
              Endpoint: 'N/A',
              SentBody: null,
              ResponseCode: result && 200,
              ResponseBody: JSON.stringify({ result, reason }),
              ResponseTime: windcavePollResponseTime,
            },
          });

          return throw_reason;
        }

        repeat -= 1;
        await delay(500);
        return await windcave_response();
      };

      const getFailureReason = await windcave_response();

      if (getFailureReason) {
        throw new Error(getFailureReason);
      }
    } catch (e) {
      console.warn('Windcave shell payment failed', { e });
      Logger.log(`Error ==> Windcave shell payment failed ${e}`);
      return rejectWithValue({
        e,
        reason: throw_reason ? throw_reason : (e as any).message,
      });
    }
  },
);
